import React, { Component, useState, useEffect } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import CustomerSearch from 'components/site/customer-search-alt';

import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import Container from 'components/v2/container';
import {
  getReasonCodesByShipper,
  createReasonCodeCategory,
  updateReasonCodeCategory,
  getDefaultReasonCodes,
} from 'utils/ReasonCodesAPI';
import Label from 'components/v2/forms/Label';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import DeleteButton from '../../v2/buttons/DeleteButton';
import Checkbox from '../../v2/forms/Checkbox';

const default_categories = [
  {
    name: 'Cancellation',
    value: 'shipment_cancellation',
    localizationSlug: 'CANCELLATION',
  },
  {
    name: 'Stop Window Change',
    value: 'stop_window_change',
    localizationSlug: 'STOP_WINDOW_CHANGED',
  },
  {
    name: 'Stop Completion',
    value: 'stop_complete',
    localizationSlug: 'STOP_COMPLETION',
  },
  {
    name: 'Stop Arrival',
    value: 'stop_arrival',
    localizationSlug: 'STOP_ARRIVAL',
  },
  {
    name: 'Accessorials',
    value: 'accessorials',
    localizationSlug: 'ACCESSORIAL',
    count: 2,
  },
  {
    name: 'Credit Memo',
    value: 'credit_memo',
    localizationSlug: 'CREDIT_MEMO',
  },
];

const getDefaultShipperPayload = (shipper, type) => {
  return {
    lineOfBusiness: type,
    shipper: shipper._id,
    categories: [
      {
        name: 'stop_complete',
        subCategories: [],
      },
      {
        name: 'stop_window_change',
        subCategories: [],
      },
      {
        name: 'stop_arrival',
        subCategories: [],
      },
      {
        name: 'shipment_cancellation',
        subCategories: [],
      },
    ],
  };
};

const ReasonCodeRow = ({
  rowInfo,
  type,
  handleCreateReasonCode,
  handleUpdateReasonCode,
  handleDeleteReasonCode,
  index,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(type === 'update' ? rowInfo.code : '');
  const [description, setDescription] = useState(
    type === 'update' ? rowInfo.description : '',
  );
  const error = false;

  useEffect(() => {
    if (rowInfo) {
      setCode(rowInfo.code);
      setDescription(rowInfo.description);
    }
  }, [rowInfo]);

  const handleReasonCodeChange = (value) => {
    setCode(value);
  };
  const handleReasonCodeDescriptionChange = (value) => setDescription(value);

  const handleCreateButton = () => async () => {
    const payload = {
      code,
      description,
    };
    handleCreateReasonCode([payload]);
    setCode('');
    setDescription('');
  };

  const handleUpdateButton = () => async () => {
    const payload = {
      code,
      description,
    };
    handleUpdateReasonCode(index, payload);
  };

  const handleDeleteButton = () => async () => {
    handleDeleteReasonCode(index);
  };

  return (
    <div className="p-2 flex flex-row justify-start">
      <CargomaticInput
        className="w-12 mr-8"
        label={t('CODE')}
        type="text"
        error={error}
        info={t('REASON_CHARACTER_CODE_INFO')}
        value={code}
        onChange={handleReasonCodeChange}
        disabled={REGION_CODE === REGIONS.AP}
      />
      <CargomaticInput
        className="w-96 mr-8"
        label={t('DESCRIPTION', { count: 1 })}
        type="text"
        info={t('HUMAN_READABLE_REASON_CODE ')}
        value={description}
        onChange={handleReasonCodeDescriptionChange}
        disabled={REGION_CODE === REGIONS.AP}
      />

      <div className="mt-6 ml-2 flex flex-row">
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          {type === 'update' ? (
            <div className="flex flex-row">
              <button
                className="button button-blue h-10 mr-2"
                disabled={error}
                onClick={handleUpdateButton()}
              >
                {t('UPDATE')}
              </button>
              <DeleteButton onClick={handleDeleteButton()} />
            </div>
          ) : (
            <button
              className="button button-green h-10 mr-2"
              onClick={handleCreateButton()}
            >
              {t('CREATE')}
            </button>
          )}
        </AuthorizationWrapper>
      </div>
      <div className="mt-8 ml-4">
        {error && (
          <span className="text-red-500 italic mt-4">
            {t('ERROR_REASON_CODE_EXISTS')}
          </span>
        )}
      </div>
    </div>
  );
};

// Form to create a reason code category
const CreateReasonCodeCategoryForm = ({ addReasonCodeSubCategory }) => {
  const { t } = useTranslation();
  const [reasonType, setReasonType] = useState(''); // Carrier Service Failure
  const [commentRequired, setCommentRequired] = useState(false);

  const handleReasonCodeTypeChange = (value) => setReasonType(value);
  const handleCommentRequiredCheckbox = (value) => setCommentRequired(value);

  const handleCreateButton = () => async () => {
    const subCategory = {
      reasonType,
      commentRequired,
      reasonCodes: [],
    };
    addReasonCodeSubCategory(subCategory);
  };

  const checkFields = () => {
    if (!reasonType || !reasonType.length) {
      return true;
    }
    return false;
  };

  return (
    <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
      <div className="w-full h-24 flex">
        <div className="w-full flex flex-row justify-start">
          <CargomaticInput
            className="w-72 mr-8"
            label={t('REASON_CODE_TYPE')}
            type="text"
            info={t('REASON_CODE_TYPE')}
            value={reasonType}
            onChange={handleReasonCodeTypeChange}
          />

          <Checkbox
            className="mt-8"
            checked={commentRequired}
            id="comment"
            onChange={handleCommentRequiredCheckbox}
          >
            {t('COMMENT_REQUIRED')}
          </Checkbox>

          <button
            disabled={checkFields()}
            className="button button-green h-10 ml-8 mt-4"
            onClick={handleCreateButton()}
          >
            {t('NEW_SUB_CATEGORY')}
          </button>
        </div>
      </div>
    </AuthorizationWrapper>
  );
};

// Form to update a reason code category
const UpdateReasonCodeCategoryForm = ({
  item,
  index,
  updateReasonCodeSubCategory,
  deleteReasonCodeSubCategory,
}) => {
  const { t } = useTranslation();
  const [reasonType, setReasonType] = useState(item.reasonType);
  const [commentRequired, setCommentRequired] = useState(item.commentRequired);
  const [reasonCodes, setReasonCodes] = useState(item.reasonCodes || []);
  const id = item._id;

  useEffect(() => {
    setReasonCodes(item.reasonCodes);
  }, [item]);

  const handleReasonCodeTypeChange = (value) => setReasonType(value);
  const handleCommentRequiredCheckbox = (value) => setCommentRequired(value);

  const handleDeleteButton = () => async () => {
    deleteReasonCodeSubCategory(index);
  };

  const checkFields = () => {
    if (!reasonType || !reasonType.length) {
      return false;
    }
    return true;
  };

  const handleUpdateButton = () => async () => {
    const isValid = checkFields();
    if (!isValid) {
      return null;
    }

    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: item.reasonCodes || [],
    };

    updateReasonCodeSubCategory(index, data);
  };

  const handleCreateReasonCode = (payload) => {
    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: [...reasonCodes, ...payload],
    };

    updateReasonCodeSubCategory(index, data);
  };

  const handleUpdateReasonCode = (codeIndex, payload) => {
    const newCodes = [...reasonCodes];
    newCodes[codeIndex] = payload;
    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: newCodes,
    };

    updateReasonCodeSubCategory(index, data);
  };

  const handleDeleteReasonCode = (codeIndex) => {
    let newCodes = [...reasonCodes];
    newCodes.splice(codeIndex, 1);

    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: newCodes,
    };
    updateReasonCodeSubCategory(index, data);
  };

  return (
    <div className="w-full flex flex-col border-black mt-8">
      <div className="w-full flex flex-row justify-start">
        <CargomaticInput
          className="w-72 mr-8"
          label={t('REASON_CODE_TYPE')}
          type="text"
          info={t('REASON_CODE_TYPE')}
          value={reasonType}
          onChange={handleReasonCodeTypeChange}
          disabled={REGION_CODE === REGIONS.AP}
        />
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          <Checkbox
            className="mt-8"
            checked={commentRequired}
            id="comment"
            onChange={handleCommentRequiredCheckbox}
          >
            {t('COMMENT_REQUIRED')}
          </Checkbox>
          <div className="mt-6 ml-8 w-48 flex flex-row">
            <button
              className="button button-blue h-10 mr-2"
              onClick={handleUpdateButton()}
            >
              {t('UPDATE')}
            </button>
            <DeleteButton onClick={handleDeleteButton()} />
          </div>
        </AuthorizationWrapper>
      </div>
      <div className="w-full flex flex-col mt-4 ml-8">
        <p className="p-2 text-grey-darker">{t('CURRENT_REASON_CODES')}:</p>
        {reasonCodes &&
          reasonCodes.map((rc, index) => (
            <ReasonCodeRow
              key={index}
              reasonCodeRow={item}
              rowInfo={rc}
              type="update"
              index={index}
              handleUpdateReasonCode={handleUpdateReasonCode}
              handleDeleteReasonCode={handleDeleteReasonCode}
            />
          ))}

        <ReasonCodeRow
          reasonCodeRow={item}
          type="create"
          handleCreateReasonCode={handleCreateReasonCode}
        />
      </div>
      <hr />
    </div>
  );
};

class ReasonCodes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: 'default',
    };
  }

  render() {
    const { t } = this.props;
    const { type } = this.state;
    const style =
      'text-gray-600 py-4 px-6 block hover:text-blue-500 focus:outline-none';
    const activeStyle =
      'text-gray-600 py-4 px-6 block hover:text-blue-500 focus:outline-none text-blue-500 border-b-2 font-medium border-blue-500';

    const usingShipper = type !== 'default';

    return (
      <Container className="py-8">
        <div className="flex flex-col">
          <div className="flex flex-col p-4 mr-8 header">
            <div className="flex flex-row w-full">
              {/* <h3 className="mt-0 mb-4 mr-2" style={{ lineHeight: '36px' }}>
                Reason Codes
              </h3> */}
              <div className="mb-8">
                <nav className="flex flex-col sm:flex-row">
                  <button
                    onClick={() => this.setState({ type: 'default' })}
                    className={type === 'default' ? activeStyle : style}
                  >
                    {t('DEFAULT_REASON_CODES')}
                  </button>
                  <button
                    onClick={() => this.setState({ type: 'custom' })}
                    className={type === 'custom' ? activeStyle : style}
                  >
                    {t('SHIPPER_REASON_CODES')}
                  </button>
                </nav>
              </div>
            </div>

            <div>
              {usingShipper ? (
                <ShipperReasonCodeForm />
              ) : (
                <DefaultReasonCodeForm />
              )}
            </div>
          </div>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(ReasonCodes);

const DefaultReasonCodeForm = withTranslation()(
  class DefaultReasonCodeForm extends Component {
    constructor(props) {
      super(props);

      this.state = {
        type: 'shorthaul',
        category: 'stop_complete',
        default_shorthaul: {},
        default_drayage: {},
        shipper: null,
        shipperInput: '',
      };
    }

    async componentDidMount() {
      await this.getReasonCodes();
    }

    /* Reason Code Categories
    - stop_complete (Stop Complete Modal)
    - stop_window_change (Add/Edit Appointment Modal)
    - shipment_cancellation (Cancel Shipment Modal)
  */

    getReasonCodes = async () => {
      try {
        const items = await getDefaultReasonCodes();
        if (!items.length) {
          return null;
        }

        const default_drayage = items.find(
          (i) => i.lineOfBusiness === 'drayage',
        );
        const default_shorthaul = items.find(
          (i) => i.lineOfBusiness === 'shorthaul',
        );

        this.setState({ default_drayage, default_shorthaul });
      } catch (e) {
        console.error('Error getting reason codes', e);
      }
    };

    handleCategorySelect = (item) => {
      this.setState({ category: item.value });
    };

    handleShipmentTypeSelect = (item) => {
      this.setState({ type: item.value });
    };

    handleAddReasonCodeSubCategory = (subCategory) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.push(subCategory);
        }
        return c;
      });

      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleUpdateSubCategory = (index, payload) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories[index] = payload;
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    deleteReasonCodeSubCategory = (index) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.splice(index, 1);
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleSave = async (codes, category) => {
      const res = await updateReasonCodeCategory(codes._id, codes);
      if (category === 'shorthaul') {
        this.setState({ default_shorthaul: res });
      } else {
        this.setState({ default_drayage: res });
      }
    };

    renderShipperFilter() {
      const { t } = this.props;
      return (
        <fieldset>
          <div className="mb-4 customer-search">
            <CustomerSearch
              clearOnSelect
              onChange={this.handleShipperFieldChange}
              onSelect={this.handleShipperSelect}
              value={this.state.shipperInput}
              placeholder={t('SEARCH_FOR_SHIPPER')}
              type="shippers"
            />
          </div>
        </fieldset>
      );
    }

    renderReasonCodeCategory() {
      const { t } = this.props;
      const { category, type, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;
      if (!codes || !codes.categories) {
        return null;
      }

      const reason_codes = codes.categories.find((c) => c.name === category);

      if (!reason_codes) {
        return <span>{t('UNABLE_TO_FIND_DEFAULT_REASON_CODE')}</span>;
      }

      return (
        <div>
          {reason_codes.subCategories.map((rc, index) => {
            return (
              <UpdateReasonCodeCategoryForm
                item={rc}
                key={index}
                index={index}
                updateReasonCodeSubCategory={this.handleUpdateSubCategory}
                getReasonCodes={this.getDefaultReasonCodes}
                deleteReasonCodeSubCategory={this.deleteReasonCodeSubCategory}
              />
            );
          })}
        </div>
      );
    }

    render() {
      const { category, type, shipper } = this.state;
      const { t } = this.props;
      if (this.props.usingShipper && !shipper) {
        return (
          <div className="mt-6 mr-8 ml-6 w-64">
            {this.renderShipperFilter()}
          </div>
        );
      }

      return (
        <div className="relative">
          <div className="flex flex-row">
            {this.props.usingShipper && (
              <div className="mt-6 mr-8">{this.renderShipperFilter()}</div>
            )}

            <div className="mb-8">
              <CargomaticSelect
                className="w-48 mr-8"
                label={t('SHIPMENT_TYPE')}
                info={t('PLEASE_SELECT_SHIPMENT_TYPE')}
                caption={t('SELECT_SHIPMENT_TYPE')}
                items={[
                  {
                    name: 'shorthaul',
                    value: 'shorthaul',
                    localizationSlug: 'SHORTHAUL',
                  },
                  {
                    name: 'drayage',
                    value: 'drayage',
                    localizationSlug: 'DRAYAGE',
                  },
                ]}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={type}
                onSelect={this.handleShipmentTypeSelect}
              />
            </div>

            <div className="mb-8">
              <CargomaticSelect
                className="w-48 mr-8"
                label={t('CATEGORY')}
                info={t('PLEASE_SELECT_REASON_CODE_CATEGORY')}
                caption={t('SELECT_A_CATEGORY')}
                items={default_categories}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={category}
                onSelect={this.handleCategorySelect}
              />
            </div>
          </div>

          <hr />

          {category === 'stop_window_change' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <span className="text-grey-darker bolder text-lg mb-6">
                {t('STOP_WINDOW_CHANGED')}
              </span>
              {this.renderReasonCodeCategory('stop_window_change')}
            </div>
          )}

          {category === 'shipment_cancellation' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('SHIPMENT_CANCELATION')}
              </p>
              {this.renderReasonCodeCategory('shipment_cancellation')}
            </div>
          )}

          {category === 'stop_complete' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('STOP_COMPLETE_REASON_CODES')}
              </p>
              {this.renderReasonCodeCategory('stop_complete')}
            </div>
          )}

          {category === 'stop_arrival' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('STOP_ARRIVAL_REASON_CODES')}
              </p>
              {this.renderReasonCodeCategory('stop_arrival')}
            </div>
          )}

          {category === 'accessorials' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('CARRIER')} {t('ACCESSORIAL', { count: 2 })}
              </p>
              {this.renderReasonCodeCategory('accessorials')}
            </div>
          )}

          {category === 'credit_memo' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('CREDIT_MEMO')}
              </p>
              {this.renderReasonCodeCategory('credit_memo')}
            </div>
          )}
        </div>
      );
    }
  },
);

const ShipperReasonCodeForm = withTranslation()(
  class ShipperReasonCodeForm extends Component {
    constructor(props) {
      super(props);

      this.state = {
        type: 'shorthaul',
        category: 'stop_complete',
        default_shorthaul: {},
        default_drayage: {},
        shipper: null,
        shipperInput: '',
        missing_codes: false,
      };
    }

    handleShipperFieldChange = (value) => {
      this.setState({ shipperInput: value });
    };

    handleShipperSelect = async (_value, item) => {
      try {
        const items = await getReasonCodesByShipper(item._id);
        if (!items || !items.length) {
          return this.setState({ shipper: item, missing_codes: true });
        }
        const default_drayage = items.find(
          (i) => i.lineOfBusiness === 'drayage',
        );
        const default_shorthaul = items.find(
          (i) => i.lineOfBusiness === 'shorthaul',
        );

        this.setState({ shipper: item, default_drayage, default_shorthaul });
      } catch (e) {
        console.error('Error getting reason codes', e);
      }
    };

    // Initialize Shipper Reason Codes
    handleCreateReasonCodesForShipper = async () => {
      const { shipper } = this.state;

      const default_shorthaul = await createReasonCodeCategory(
        getDefaultShipperPayload(shipper, 'shorthaul'),
      );
      const default_drayage = await createReasonCodeCategory(
        getDefaultShipperPayload(shipper, 'drayage'),
      );
      this.setState({
        missing_codes: false,
        default_drayage,
        default_shorthaul,
      });
    };

    handleCategorySelect = (item) => {
      this.setState({ category: item.value });
    };

    handleShipmentTypeSelect = (item) => {
      this.setState({ type: item.value });
    };

    handleAddReasonCodeSubCategory = (subCategory) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.push(subCategory);
        }
        return c;
      });

      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleUpdateSubCategory = (index, payload) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories[index] = payload;
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    deleteReasonCodeSubCategory = (index) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.splice(index, 1);
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleSave = async (codes, category) => {
      const items = await updateReasonCodeCategory(codes._id, codes);

      if (category === 'shorthaul') {
        this.setState({ default_shorthaul: items });
      } else {
        this.setState({ default_drayage: items });
      }
    };

    renderShipperFilter() {
      const { t } = this.props;
      return (
        <fieldset>
          <div className="mb-4 mt-1 customer-search">
            <Label helptext={t('CURRENT_SELECTED_SHIPPER')}>
              {t('SHIPPER')}:{' '}
              {this.state.shipper ? this.state.shipper.name : t('NONE')}
            </Label>
            <CustomerSearch
              clearOnSelect
              onChange={this.handleShipperFieldChange}
              onSelect={this.handleShipperSelect}
              value={this.state.shipperInput}
              placeholder={t('SEARCH_FOR_SHIPPER')}
              type="shippers"
            />
          </div>
        </fieldset>
      );
    }

    renderReasonCodeCategory() {
      const { category, type, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;
      const { t } = this.props;
      if (!codes || !codes.categories) {
        return null;
      }

      const reason_codes = codes.categories.find((c) => c.name === category);

      if (!reason_codes) {
        return <span>{t('UNABLE_TO_FIND_DEFAULT_REASON_CODE')}</span>;
      }

      return (
        <div>
          {reason_codes.subCategories.map((rc, index) => {
            return (
              <UpdateReasonCodeCategoryForm
                item={rc}
                key={index}
                index={index}
                updateReasonCodeSubCategory={this.handleUpdateSubCategory}
                getReasonCodes={this.getDefaultReasonCodes}
                deleteReasonCodeSubCategory={this.deleteReasonCodeSubCategory}
              />
            );
          })}
        </div>
      );
    }

    render() {
      const { category, type, shipper, missing_codes } = this.state;
      const { t } = this.props;

      if (!shipper) {
        return (
          <div className="mt-6 mr-8 ml-6 w-64">
            {this.renderShipperFilter()}
          </div>
        );
      }

      if (shipper && missing_codes) {
        return (
          <div className="mt-6 mr-8 ml-6 w-64">
            {this.renderShipperFilter()}
            <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
              <button
                className="button button-green h-14 mt-6"
                onClick={this.handleCreateReasonCodesForShipper}
              >
                {t('CREATE_CUSTOM_REASON_CODE_FOR_SHIPPER')}
              </button>
            </AuthorizationWrapper>
          </div>
        );
      }

      return (
        <div className="relative">
          <div className="flex flex-row">
            <div className="mr-8">{this.renderShipperFilter()}</div>

            <div className="mb-8">
              <CargomaticSelect
                className="w-48 mr-8"
                label={t('SHIPMENT_TYPE')}
                info={t('PLEASE_SELECT_SHIPMENT_TYPE')}
                caption={t('SELECT_SHIPMENT_TYPE')}
                items={[
                  {
                    name: 'shorthaul',
                    value: 'shorthaul',
                    localizationSlug: 'SHORTHAUL',
                  },
                  {
                    name: 'drayage',
                    value: 'drayage',
                    localizationSlug: 'DRAYAGE',
                  },
                ]}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={type}
                onSelect={this.handleShipmentTypeSelect}
              />
            </div>

            <div className="mb-8">
              <CargomaticSelect
                className="w-48 mr-8"
                label={t('CATEGORY')}
                info={t('PLEASE_SELECT_REASON_CODE_CATEGORY')}
                caption={t('PLEASE_SELECT_REASON_CODE_CATEGORY')}
                items={default_categories}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={category}
                onSelect={this.handleCategorySelect}
              />
            </div>
          </div>

          <hr />

          {category === 'stop_window_change' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <span className="text-grey-darker bolder text-lg mb-6">
                {t('STOP_WINDOW_CHANGED')}
              </span>
              {this.renderReasonCodeCategory('stop_window_change')}
            </div>
          )}

          {category === 'shipment_cancellation' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('SHIPMENT_CANCELATION')}
              </p>
              {this.renderReasonCodeCategory('shipment_cancellation')}
            </div>
          )}

          {category === 'stop_complete' && (
            <div className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p className="text-grey-darker bolder text-lg mb-6">
                {t('STOP_COMPLETE_REASON_CODES')}
              </p>
              {this.renderReasonCodeCategory('stop_complete')}
            </div>
          )}
        </div>
      );
    }
  },
);
